import * as React from 'react'
import Image from 'next/image'
import { WizardScheduleServiceValues } from '../Forms/ScheduleServiceMultiStep/NoEmailScheduleServiceFormMultiStep'
import { Container } from '../Container'
import { StarsAndRatings } from './StarsAndRatings'
import * as Sentry from '@sentry/nextjs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import googleLogoIcon from '../../../public/img/icons/google-logo-icon.png'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import backgroundImage from '../../../public/img/nubrakes-van.png'
import viocLogo from '../../../public/img/vioc-logo.png'
import { GraySection } from '../GraySection'
import { CtaButton } from './CtaButton'
import { createViocLead } from './createViocLead'
import { getExtraHiddenFieldValues } from '../Fields/LeadExtraFields'
import { useQueryParam } from '../../hooks/useQueryParam'
import { CUSTOMER_WEBAPP_URL } from '../../pages/_app'
import { serializeLinkWithParams } from '../../util/util'
import { VIOC_EXPERIMENT_ASSIGNMENT_ENABLED, VIOC_EXPERIMENT_COOKIE } from '../../lib/viocExperiment'

export const phoneToInts = (maskedPhone: string) => {
  return maskedPhone.replace(/\D/g, '')
}

const schema = yup
  .object({
    phoneNumber: yup.string().notRequired(),
  })
  .required()

interface FormData {
  phoneNumber: string
}

const defaultHeaderCopy = {
  subtitle: 'Preferred Brake Partner',
  bullets: [
    'We come to you — or meet at Valvoline',
    'Free Brake Inspection ($79 Value)',
    'Completed in 20 minutes',
  ] as const,
  placeholder: 'Enter your Phone Number here',
  scheduleButton: 'SCHEDULE INSPECTION',
  errorMessage: 'Something went wrong. Please try again.',
  reviewsLabel: '(14,270 reviews)',
}

type Props = {
  title: string
  subtitle?: string
  bullets?: readonly [string, string, string]
  placeholder?: string
  scheduleButton?: string
  errorMessage?: string
  reviewsLabel?: string
  rating?: string
  reviewCount?: number
  formInitialProps?: WizardScheduleServiceValues
  sessionId?: string
}

const goToLink = (link: string) => window['location'].assign(link)

const VIOC_LEAD_FORM_URL = `${
  CUSTOMER_WEBAPP_URL.includes('localhost') ? 'http' : 'https'
}://${CUSTOMER_WEBAPP_URL}/vioc/initial-details`

export const HeaderSection: React.FC<Props> = ({
  title,
  subtitle = defaultHeaderCopy.subtitle,
  bullets = defaultHeaderCopy.bullets,
  scheduleButton = defaultHeaderCopy.scheduleButton,
  errorMessage: errorMessageProp = defaultHeaderCopy.errorMessage,
  reviewsLabel = defaultHeaderCopy.reviewsLabel,
}) => {
  const query = useQueryParam()
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const { handleSubmit } = useForm<FormData>({
    resolver: yupResolver(schema),
  })
  const [, setIsLoading] = React.useState(false)
  // const onFormValidationSuccess = async (data: FormData) => {
  //   setIsLoading(true)
  //   setErrorMessage(null)
  //   try {
  //     const phoneInts = phoneToInts(data?.phoneNumber)
  //     await createViocLead({
  //       phoneNumber: phoneInts,
  //       extraFields: getExtraHiddenFieldValues(),
  //     })
  //     goToLink(
  //       serializeLinkWithParams(VIOC_LEAD_FORM_URL, {
  //         ...query,
  //         phoneNumber: phoneInts,
  //       })
  //     )
  //   } catch (e) {
  //     setErrorMessage(errorMessageProp)
  //     Sentry.captureException(e, {
  //       extra: {
  //         phoneNumber: data?.phoneNumber,
  //       },
  //       tags: {
  //         fn: 'createViocLead',
  //       },
  //     })
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }
  const onFormValidationSuccess = async () => {
    setIsLoading(true)
    setErrorMessage(null)
    try {
      await createViocLead({
        extraFields: getExtraHiddenFieldValues(),
      })
      // Only forward the assigned variant while the experiment is live. During
      // preview the customer webapp demos a variant via a hand-typed ?uiv=, so
      // this repo must not append one. See ../../lib/viocExperiment.
      const uiv = VIOC_EXPERIMENT_ASSIGNMENT_ENABLED
        ? document.cookie
            .split('; ')
            .find((row) => row.startsWith(`${VIOC_EXPERIMENT_COOKIE}=`))
            ?.split('=')[1]
        : undefined
      goToLink(
        serializeLinkWithParams(VIOC_LEAD_FORM_URL, {
          ...query,
          ...(uiv && { uiv }),
        })
      )
    } catch (e) {
      setErrorMessage(errorMessageProp)
      Sentry.captureException(e, {
        tags: {
          fn: 'createViocLead',
        },
      })
    } finally {
      setIsLoading(false)
    }
  }
  const onSubmitForm = () => {
    handleSubmit(onFormValidationSuccess, (errors) => {
      console.error(errors)
    })()
  }

  return (
    <GraySection className="py-0 text-white">
      <div className="py-6 lg:py-12 flex flex-col justify-center items-center  relative w-full h-full ">
        <div
          id="overlay"
          style={{
            minHeight: '100%',
            width: '100%',
            position: 'absolute',
            zIndex: 1,
            backgroundColor: 'rgba(0,0,0,.75)',
          }}
        />
        <Image
          src={backgroundImage}
          placeholder="blur"
          layout={'fill'}
          objectFit="cover"
          objectPosition="bottom center"
          priority={true}
          alt="NuBrakes Mobile Repair Header Image"
        />
        <div style={{ zIndex: 2 }}>
          <Container className="text-center">
            <div className="flex flex-col items-center">
              <Image
                src={viocLogo}
                objectPosition="bottom center"
                height={62}
                width={70}
                priority={true}
                alt="NuBrakes Mobile Repair Header Image"
              />

              <h3 className="text-lg mt-2 text-[#FFFFFF] opacity-50 mb-4">{subtitle}</h3>

              <div className="text-center flex flex-col justify-center items-center">
                <h1 className="text-2xl mt-4 md:mt-0 px-4 md:px-0 sm:text-2xl md:text-4xl text-white sm:mb-0">
                  {title}
                </h1>
                <div className="list-none flex py-4 flex-wrap gap-x-4 max-w-[36rem] justify-center items-center">
                  {bullets.map((item) => (
                    <div key={item} className="text-white flex flex-row justify-center items-center">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        size="1x"
                        color={'#14d90d'}
                        style={{ borderRadius: '100%', backgroundColor: 'white' }}
                      />
                      <p className="ml-2 mt-1">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </div>

        <Container className="z-[2] w-full max-w-[36rem] text-center -mb-[160px]">
          <div className="flex flex-col items-center justify-center shadow-lg bg-white rounded-md">
            <div style={{ zIndex: 2 }} className="flex flex-col  p-4 pb-6 max-w-[36rem] w-full">
              {errorMessage ? <p className="text-red-500">{errorMessage}</p> : null}
              {/*<div className="relative text-gray-600 focus-within:text-gray-400 w-full">
                <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                  <FontAwesomeIcon
                    icon={faPhone}
                    color="black"
                    className="absolute top-1/2 left-4 transform -translate-y-1/2"
                  />
                </span>
                <input
                  {...phoneField}
                  ref={mergeRefs(phoneField.ref, maskRef)}
                  className={`py-3 w-full border ${
                    errors?.phoneNumber?.message ? 'border-red-500' : ''
                  }  text-base text-gray-500 bg-white rounded-md pl-11 focus:outline-none focus:bg-white focus:text-gray-900`}
                  placeholder={placeholder}
                  type='tel'
                /> 
              </div>*/}
              {/*errors?.phoneNumber?.message ? (
                <span className="text-xs pl-2 pt-1 text-red-500 justify-start w-full  text-left">
                  {errors?.phoneNumber?.message}
                </span>
              ) : null*/}
              <CtaButton
                //isLoading={isSubmitting || isLoading}
                //disabled={isSubmitting || isLoading}
                onClick={onSubmitForm}
                buttonText={scheduleButton}
                className="w-full mt-4"
              />
              <div className="flex mt-4 justify-center items-center text-black flex-wrap">
                <span className="mr-3" style={{ fontFamily: 'IndustryInc-Base' }}>
                  4.9
                </span>
                <StarsAndRatings />
                <div className="flex ml-3 items-center">
                  <div>{reviewsLabel}</div>
                  <div className="ml-2 flex items-center justify-center w-5 h-5 rounded-full">
                    <Image src={googleLogoIcon} objectPosition="bottom center" priority={true} alt="Valvoline logo" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>
      <div className="h-[140px]" />
    </GraySection>
  )
}

export default HeaderSection
